import Input from "@ui-kit/form/Input/Input.web";
import { useTranslation } from "@shared/utils/internationalization";
import useUpdateEffect from "@shared/hooks/useUpdateEffect/useUpdateEffect";
import {
  forwardRef,
  useCallback,
  useDeferredValue,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  CardExpiryChangeEvent,
  CardExpiryInputProps,
} from "./CardExpiryInput.props";
import {
  formatCompactExpiry,
  formatSlashedExpiry,
  formatSpacedExpiry,
  prepExpiryForMonthInput,
  prepExpiryForYearInput,
} from "./utils/cardExpiryFormat";

const CardExpiryInput = forwardRef<
  HTMLInputElement | null,
  CardExpiryInputProps
>(function CardExpiryInput({ onChange }, ref) {
  const { t } = useTranslation("checkout");

  const inputRef = useRef<HTMLInputElement>(null);

  const [cardExpiry, setCardExpiry] = useState("");

  // Deferred value of card expiry for complementary logic
  // such as onChange callbacks
  const deferredCardExpiry = useDeferredValue(cardExpiry);
  const onChangeObjCache = useRef<CardExpiryChangeEvent>({
    complete: false,
    error: null,
  });

  const isInputComplete = useCallback((input: string) => {
    if (!input) return false;

    const [month, year] = input.split("/");
    if (!month || !year) {
      return false;
    }

    if (Number.isNaN(Number(month)) || Number.isNaN(Number(year))) {
      return false;
    }

    return true;
  }, []);

  // Side effect that calls onChange when the card expiry changes
  useUpdateEffect(() => {
    const complete = isInputComplete(deferredCardExpiry);

    onChangeObjCache.current = {
      ...onChangeObjCache.current,
      complete,
    };

    onChange?.(onChangeObjCache.current);
  }, [onChange, isInputComplete, deferredCardExpiry]);

  /**
   * Intercept the input event to replace the resulting value with more legible formatting.
   * Our formatting is done to funnel user behaviour in between month/year inputs
   * without splitting them into 2 separate inputs.
   *
   *
   * The exposed `onChange` API of this component is not a true onChange.
   * It is not passed into the input function. Since "input" events behave like React "onChange" events,
   * we intercept the "input" event, but expose an `onChange` API triggered by an "input" event.
   */
  const interceptOnInput = useCallback((e: Event) => {
    const ev = e as InputEvent;
    const target = e.target as HTMLInputElement;
    let { value } = target;
    const cursorPosition = target.selectionStart || 0;

    if (ev.inputType !== "deleteContentBackward") {
      // Remove all non-digit (0-9), non "/" (/), non white-space (/s) characters
      value = value.trim().replace(/[^0-9/\s]/g, "");

      // For pure number inputs (1224)
      if (!Number.isNaN(Number(value))) {
        //  Format it correctly
        if (value.length > 2) {
          value = formatCompactExpiry(value);
        }
      }

      // For inputs with an existing "/"
      if (value.includes("/") && !value.includes(" / ")) {
        value = formatSlashedExpiry(value);
      }
      // Otherwise insert the slash immediately after the second digit
      else if (value.length >= 2 && value.length < 5) {
        value = prepExpiryForYearInput(value);
      }

      // For inputs that must have some form of MM entered ("6 / " or "06 / ")
      if (value.includes(" / ")) {
        value = formatSpacedExpiry(value);
      }
    } else if (ev.inputType === "deleteContentBackward") {
      // Remove the slash in it's entirety when hitting backspace
      if (value.length >= 2 && value.length <= 5) {
        value = prepExpiryForMonthInput(value);
      }
    }

    // Update state with new value
    setCardExpiry(value);

    // Move the cursor correctly when removing the slash
    setTimeout(() => {
      const input = inputRef.current;
      if (input) {
        if (
          cursorPosition > 2 &&
          cursorPosition <= 5 &&
          ev.inputType === "deleteContentBackward"
        ) {
          input.setSelectionRange(cursorPosition - 1, cursorPosition - 1);
        }
      }
    }, 0);
  }, []);

  /**
   * Intercept the keydown event in order to programmatically change the input event value
   */
  const interceptKeydown = useCallback((e: Event) => {
    const ev = e as Omit<KeyboardEvent, "key"> & { key: string | undefined }; // `key` may not always be in a `keydown` event. eg. Autofill
    const target = e.target as HTMLInputElement;
    const { value } = target;

    if (ev.key) {
      // When a character is pressed (instead of a command)
      if (ev.key.length === 1 && !(ev.metaKey || ev.ctrlKey)) {
        // Enforce char limit
        if (value.length === 7) {
          e.preventDefault();
        }
      }

      // When "/" is entered
      if (ev.key === "/") {
        // ignore the event
        e.preventDefault();

        //                                                                                      v
        // And if there is a single char already in the input, move on to the 3rd digit ( MM / [Y]Y )
        if (value.trim().length === 1 && value.trim() !== "0") {
          target.value = `0${value} / `;
        }
      }
    }
  }, []);

  /**
   * Validates a card expiry date based on the `expiry` state.
   * Fires an onChange event regardless of the outcome
   */
  const validateInput = useCallback(() => {
    let error: CardExpiryChangeEvent["error"] = null;

    const [monthStr, yearStr] = cardExpiry.split(" / ");

    if (!monthStr || !yearStr) {
      error = {
        type: "validation_error",
        code: "invalid_expiry_month",
        message: t("Your card's expiration date is incomplete."),
      };
    }

    // Convert month and year to integers
    const expiryMonth = Number(monthStr);
    const expiryYear = Number(yearStr) + 2000; // Convert YY to YYYY (e.g., 23 becomes 2023)

    // Early return if the month is invalid
    if (expiryMonth < 1 || expiryMonth > 12) {
      error = {
        type: "validation_error",
        code: "invalid_expiry_month",
        message: t("Your card's expiration date is invalid."),
      };
    }

    // Get current month and year without creating a Date object
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1; // Months are 0-indexed, so add 1

    // Check if the year is in the past
    if (expiryYear < currentYear) {
      error = {
        type: "validation_error",
        code: "expired_card",
        message: t("Your card's expiration year is in the past."),
      };
    }

    // If the year is the current year, check if the month is in the past
    if (expiryYear === currentYear && expiryMonth < currentMonth) {
      error = {
        type: "validation_error",
        code: "expired_month",
        message: t("Your card's expiration date is in the past."),
      };
    }

    onChangeObjCache.current = {
      ...onChangeObjCache.current,
      error,
    };

    onChange?.(onChangeObjCache.current);
  }, [t, onChange, cardExpiry]);

  /**
   * Add our input event interceptor via addEventListener.
   * This allows other event listeners to be added without being overridden by an explicity `onInput` or `onChange`
   * React element attribute.
   */
  useEffect(() => {
    inputRef.current?.addEventListener("input", interceptOnInput);

    return () =>
      inputRef.current?.removeEventListener("input", interceptOnInput);
  }, [interceptOnInput]);

  /**
   * Add our keydown event interceptor via addEventListener.
   *
   * We use the keydown event to funnel users into a specific input flow of the expiration date
   * using a single <input> element.
   */
  useEffect(() => {
    inputRef.current?.addEventListener("keydown", interceptKeydown);

    return () =>
      inputRef.current?.removeEventListener("keydown", interceptKeydown);
  }, [interceptKeydown]);

  /**
   * Add our blur event interceptor via addEventListener.
   *
   * We use the blur event to trigger our validation
   */
  useEffect(() => {
    inputRef.current?.addEventListener("blur", validateInput);

    return () => inputRef.current?.removeEventListener("blur", validateInput);
  }, [validateInput]);

  /**
   * Expose the internal input ref
   */
  useImperativeHandle<
    HTMLInputElement | null,
    HTMLInputElement | null
  >(ref, () => inputRef.current, []);

  return (
    <Input
      w="100%"
      h="element.3xl"
      lineHeight="1.2em"
      ref={inputRef}
      placeholder={t("MM / YY")}
      value={cardExpiry}
      readOnly={false}
      variant="unstyled"
      outline="none"
      maxLength={2 + 3 + 2} // MM + _/_ + YY
      // @see https://web.dev/learn/forms/payment#help_users_enter_their_payment_details
      type="text"
      inputMode="numeric"
      autoComplete="cc-exp"
    />
  );
});

export default CardExpiryInput;
